import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { doc, serverTimestamp, updateDoc, writeBatch } from "firebase/firestore";
import { deleteUser } from "firebase/auth";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";

import { db, storage, auth } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import { getEarnedTrophies } from "../../data/trophyCatalog";
import AvatarWithTrophy from "../../components/profile/AvatarWithTrophy";
import PipWardrobe from "../../components/Pip/PipWardrobe";

import "../../screens/UserProfileEdit.css";

const STROKE = 1.8;
const BIO_MAX = 160;

function Toggle({ on, onChange, label }) {
  return (
    <button
      type="button"
      className={`upe-toggle ${on ? "on" : "off"}`}
      onClick={() => onChange(!on)}
      aria-label={label}
      aria-pressed={on}
    >
      <span className="upe-toggle-thumb" />
    </button>
  );
}

export default function FanProfileEdit() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile, logout, userPerks, refreshUserPerks } = useAuth();

  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [avatarPreview, setAvatarPreview] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const [pinnedTrophy, setPinnedTrophy] = useState(null);
  const [notifDrops, setNotifDrops] = useState(true);
  const [notifCredits, setNotifCredits] = useState(true);
  const [notifWall, setNotifWall] = useState(false);
  const [trophyVisibility, setTrophyVisibility] = useState("public");
  const [collectionVisibility, setCollectionVisibility] = useState("private");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const avatarInputRef = useRef(null);
  const earnedTrophies = getEarnedTrophies(userProfile || {});

  const profilePath = currentUser ? `/fan/${currentUser.uid}` : "/profile";
  const isOwner = currentUser && userId === currentUser.uid;

  useEffect(() => {
    if (!userProfile) return;
    setDisplayName(userProfile.displayName || "");
    setBio(userProfile.bio || "");
    setAvatarPreview(userProfile.avatar || userProfile.avatarUrl || "");
    setPinnedTrophy(userProfile.pinnedTrophy || null);
    setNotifDrops(userProfile.notifications?.drops ?? true);
    setNotifCredits(userProfile.notifications?.credits ?? true);
    setNotifWall(userProfile.notifications?.wall ?? false);
    setTrophyVisibility(userProfile.privacy?.trophyCase ?? "public");
    setCollectionVisibility(userProfile.privacy?.privateCollection ?? "private");
  }, [userProfile]);

  if (!currentUser) return <Navigate to="/welcome?auth=signin" replace />;
  if (!isOwner) return <Navigate to={profilePath} replace />;

  const showToast = (text) => {
    setToast(text);
    setTimeout(() => setToast(null), 3000);
  };

  const handleSave = async () => {
    if (bio.length > BIO_MAX) {
      showToast(`Bio must be ${BIO_MAX} characters or less`);
      return;
    }
    setSaving(true);
    try {
      let avatarUrl = avatarPreview;
      if (avatarFile) {
        const path = `avatars/${currentUser.uid}/${Date.now()}-${avatarFile.name.replace(/\s+/g, "-")}`;
        const fileRef = ref(storage, path);
        await uploadBytes(fileRef, avatarFile);
        avatarUrl = await getDownloadURL(fileRef);
      }

      const batch = writeBatch(db);
      const userRef = doc(db, "users", currentUser.uid);
      batch.update(userRef, {
        displayName: displayName.trim(),
        bio: bio.trim(),
        avatar: avatarUrl,
        avatarUrl,
        pinnedTrophy: pinnedTrophy || null,
        "notifications.drops": notifDrops,
        "notifications.credits": notifCredits,
        "notifications.wall": notifWall,
        privacy: {
          trophyCase: trophyVisibility,
          privateCollection: collectionVisibility,
        },
        updatedAt: serverTimestamp(),
      });
      await batch.commit();
      showToast("Profile saved!");
      setTimeout(() => navigate(profilePath), 800);
    } catch (err) {
      console.error("Save error:", err);
      showToast("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await updateDoc(doc(db, "users", currentUser.uid), {
        deleted: true,
        deletedAt: serverTimestamp(),
      });
      await deleteUser(auth.currentUser);
      logout?.();
      navigate("/welcome");
    } catch (err) {
      console.error("Delete account error:", err);
      showToast("Could not delete account. You may need to sign in again.");
      setShowDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="upe-page">
      <nav className="upe-nav">
        <Link to={profilePath} className="upe-nav-back">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round"><polyline points="15 18 9 12 15 6" /></svg>
          Back to profile
        </Link>
        <div className="upe-nav-logo">100<em>Views</em><span>Only</span></div>
        <div style={{ width: 120 }} />
      </nav>

      <div className="upe-content">
        <header className="upe-page-header">
          <div className="upe-page-eyebrow">Account settings</div>
          <h1 className="upe-page-title">Edit your <em>profile</em></h1>
        </header>

        <section className="upe-section">
          <h2 className="upe-section-title">Profile photo</h2>
          <div className="upe-avatar-row">
            <div className="upe-avatar-preview">
              <AvatarWithTrophy src={avatarPreview} alt="" imgClassName="upe-avatar-img" pinnedTrophy={pinnedTrophy} />
              <button type="button" className="upe-avatar-upload-btn" onClick={() => avatarInputRef.current?.click()} aria-label="Upload avatar">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={STROKE} strokeLinecap="round"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" /><circle cx="12" cy="13" r="4" /></svg>
              </button>
              <input ref={avatarInputRef} type="file" accept="image/*" hidden onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) { setAvatarFile(f); setAvatarPreview(URL.createObjectURL(f)); }
              }} />
            </div>
            <div className="upe-avatar-info">
              <p>Your photo appears on your profile, comments, and the Appreciation Wall.</p>
              <small>JPG or PNG · max 5 MB</small>
              <button type="button" className="upe-upload-btn" onClick={() => avatarInputRef.current?.click()}>Upload photo</button>
            </div>
          </div>
        </section>

        <section className="upe-section">
          <h2 className="upe-section-title">Basic info</h2>
          <div className="upe-field">
            <label htmlFor="upe-display-name">Display name</label>
            <input id="upe-display-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </div>
          <div className="upe-field">
            <label htmlFor="upe-bio">Bio</label>
            <textarea id="upe-bio" rows={3} maxLength={BIO_MAX} value={bio} onChange={(e) => setBio(e.target.value)} />
            <div className={`upe-char-count${bio.length >= BIO_MAX ? " over" : bio.length > BIO_MAX * 0.9 ? " warn" : ""}`}>{bio.length}/{BIO_MAX}</div>
          </div>
        </section>

        <section className="upe-section">
          <h2 className="upe-section-title">Pinned trophy</h2>
          <div className="upe-trophy-grid">
            <button type="button" className={`upe-trophy-none${!pinnedTrophy ? " selected" : ""}`} onClick={() => setPinnedTrophy(null)}>
              <span>No pin</span>
            </button>
            {earnedTrophies.map((trophy) => (
              <button
                key={trophy.key}
                type="button"
                className={`upe-trophy-item${pinnedTrophy === trophy.key ? " selected" : ""}`}
                onClick={() => setPinnedTrophy(trophy.key)}
              >
                <div className="upe-trophy-icon" style={{ background: trophy.iconBg }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={trophy.iconStroke} strokeWidth={STROKE} strokeLinecap="round"><path d="M6 9H4.5a2.5 2.5 0 010-5H6" /><path d="M18 9h1.5a2.5 2.5 0 000-5H18" /><path d="M4 22h16" /><path d="M18 2H6v7a6 6 0 0012 0V2z" /></svg>
                </div>
                <span className="upe-trophy-name">{trophy.name}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="upe-section">
          <h2 className="upe-section-title">Notifications</h2>
          <div className="upe-toggle-row">
            <div className="upe-toggle-info"><strong>Drop alerts</strong><p>When creators you follow post new drops</p></div>
            <Toggle on={notifDrops} onChange={setNotifDrops} label="Drop alerts" />
          </div>
          <div className="upe-toggle-row">
            <div className="upe-toggle-info"><strong>Credit rewards</strong><p>When you earn credits from streaks or challenges</p></div>
            <Toggle on={notifCredits} onChange={setNotifCredits} label="Credit rewards" />
          </div>
          <div className="upe-toggle-row">
            <div className="upe-toggle-info"><strong>Wall replies</strong><p>When a creator responds to your Appreciation Wall note</p></div>
            <Toggle on={notifWall} onChange={setNotifWall} label="Wall replies" />
          </div>
        </section>

        <section className="upe-section">
          <h2 className="upe-section-title">Privacy</h2>
          <div className="upe-privacy-option" onClick={() => setTrophyVisibility("public")} role="button" tabIndex={0}>
            <span className={`upe-radio${trophyVisibility === "public" ? " selected" : ""}`}>{trophyVisibility === "public" && <span className="upe-radio-dot" />}</span>
            <div><strong>Trophy Case — Public</strong><p>Anyone can see your earned trophies on your profile</p></div>
          </div>
          <div className="upe-privacy-option" onClick={() => setTrophyVisibility("private")} role="button" tabIndex={0}>
            <span className={`upe-radio${trophyVisibility === "private" ? " selected" : ""}`}>{trophyVisibility === "private" && <span className="upe-radio-dot" />}</span>
            <div><strong>Trophy Case — Private</strong><p>Only you can see your trophies</p></div>
          </div>
          <div className="upe-privacy-option" onClick={() => setCollectionVisibility("public")} role="button" tabIndex={0}>
            <span className={`upe-radio${collectionVisibility === "public" ? " selected" : ""}`}>{collectionVisibility === "public" && <span className="upe-radio-dot" />}</span>
            <div><strong>Private Collection — Public</strong><p>Other fans can browse your unlocked drops</p></div>
          </div>
          <div className="upe-privacy-option" onClick={() => setCollectionVisibility("private")} role="button" tabIndex={0}>
            <span className={`upe-radio${collectionVisibility === "private" ? " selected" : ""}`}>{collectionVisibility === "private" && <span className="upe-radio-dot" />}</span>
            <div><strong>Private Collection — Private</strong><p>Only you can see your collection (default)</p></div>
          </div>
        </section>

        <PipWardrobe
          userId={currentUser.uid}
          userPerks={userPerks}
          onRefresh={() => refreshUserPerks?.(currentUser.uid)}
        />

        <section className="upe-danger-section">
          <h2 className="upe-danger-title">Danger zone</h2>
          <div className="upe-danger-row">
            <p>Permanently delete your account and all associated data. This cannot be undone.</p>
            <button type="button" className="upe-danger-btn" onClick={() => setShowDeleteModal(true)}>Delete account</button>
          </div>
        </section>

        <div className="upe-action-row">
          <button type="button" className="upe-btn-cancel" onClick={() => navigate(profilePath)}>Cancel</button>
          <button type="button" className="upe-btn-save" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {showDeleteModal && (
        <div className="upe-modal" role="dialog" aria-modal="true">
          <div className="upe-modal-backdrop" onClick={() => setShowDeleteModal(false)} />
          <div className="upe-modal-card">
            <h3>Delete your account?</h3>
            <p>This permanently removes your profile, collection, and trophies.</p>
            <div className="upe-modal-actions">
              <button type="button" className="upe-btn-cancel" onClick={() => setShowDeleteModal(false)}>Keep account</button>
              <button type="button" className="upe-danger-btn" onClick={handleDeleteAccount} disabled={deleting}>
                {deleting ? "Deleting…" : "Yes, delete forever"}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="upe-toast">{toast}</div>}
    </div>
  );
}
